/**
 * @fileoverview What an outbound click is FOR (LIFE-SHORTLISTED-001 P9.3) — the `destinationKind`
 * property `outbound_clicked` carries, so a report can say "newsletter sign-ups" or "shop visits"
 * rather than listing URLs.
 *
 * Order of evidence:
 *   1. the SCHEME — `mailto:` is email and `tel:` is a phone call, whatever a caller declares;
 *   2. a DECLARED web kind — the author knows a link is the shop or an article (a site link's kind,
 *      a collection item's destination type), which no host can tell;
 *   3. a KNOWN HOST — the social platforms `<SocialIcon>` draws, and Substack as a newsletter;
 *   4. otherwise plainly `external`.
 * A host matches exactly or as a subdomain — `notinstagram.com` is not Instagram.
 *
 * @module @puffin-core/marketing-shell/links/destinationKind
 */

import { HREF_KIND, classifyHref } from '@puffin-core/block-schema/href';

/** The destination kinds an `outbound_clicked` event reports. */
export const DESTINATION_KIND = Object.freeze({
  EMAIL: 'email',
  PHONE: 'phone',
  SOCIAL: 'social',
  NEWSLETTER: 'newsletter',
  SHOP: 'shop',
  ARTICLE: 'article',
  EXTERNAL: 'external',
});

/** The kinds a caller may declare for a WEB destination. */
const DECLARABLE_WEB_KINDS = Object.freeze([
  DESTINATION_KIND.SOCIAL,
  DESTINATION_KIND.NEWSLETTER,
  DESTINATION_KIND.SHOP,
  DESTINATION_KIND.ARTICLE,
  DESTINATION_KIND.EXTERNAL,
]);

/** Hosts whose kind is known from the host alone: `[host, kind]`, matched exactly or as a subdomain. */
const KNOWN_HOSTS = Object.freeze([
  ['instagram.com', DESTINATION_KIND.SOCIAL],
  ['pinterest.com', DESTINATION_KIND.SOCIAL],
  ['pin.it', DESTINATION_KIND.SOCIAL],
  ['tiktok.com', DESTINATION_KIND.SOCIAL],
  ['youtube.com', DESTINATION_KIND.SOCIAL],
  ['youtu.be', DESTINATION_KIND.SOCIAL],
  ['threads.net', DESTINATION_KIND.SOCIAL],
  ['facebook.com', DESTINATION_KIND.SOCIAL],
  ['x.com', DESTINATION_KIND.SOCIAL],
  ['twitter.com', DESTINATION_KIND.SOCIAL],
  ['substack.com', DESTINATION_KIND.NEWSLETTER],
]);

/**
 * @param {string} href - a web URL
 * @returns {?string} the kind its host implies, or null
 */
function kindOfHost(href) {
  let host;
  try {
    host = new URL(href).hostname;
  } catch (error) {
    // `new URL` answers an address it cannot read (a relative or malformed author-written link)
    // with a TypeError: no host, so no host-implied kind — the caller reports `external`. Anything
    // else is a defect (SWALLOWED-CATCH-BURNDOWN-001 U3). Read by NAME: a URL implementation from
    // another realm throws that realm's TypeError.
    if (error?.name !== 'TypeError') throw error;
    return null;
  }
  const match = KNOWN_HOSTS.find(([known]) => host === known || host.endsWith(`.${known}`));
  return match ? match[1] : null;
}

/**
 * @param {string} href - the destination (already known to leave the site)
 * @param {string} [declared] - the kind the link's author declared, if any
 * @returns {string} one of `DESTINATION_KIND`
 */
export function outboundDestinationKind(href, declared) {
  const scheme = classifyHref(href);
  if (scheme === HREF_KIND.MAILTO) return DESTINATION_KIND.EMAIL;
  if (scheme === HREF_KIND.TEL) return DESTINATION_KIND.PHONE;
  if (DECLARABLE_WEB_KINDS.includes(declared)) return declared;
  return kindOfHost(href) || DESTINATION_KIND.EXTERNAL;
}
